import axios from 'axios';

// Function to get API base URL for auth API
function getAuthApiBaseUrl() {
  // Для локальной разработки используем текущий origin через nginx
  const isLocalhost = window.location.hostname === 'localhost' || 
                      window.location.hostname === '127.0.0.1' ||
                      window.location.hostname === '';
  
  if (isLocalhost) {
    // Используем текущий origin (localhost через nginx на порту 80)
    // Nginx проксирует запросы к Django на порт 8000
    return window.location.origin;
  }
  
  // Если указан явный URL в env - используем его
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '');
  }
  
  // Для продакшена используем текущий домен
  return window.location.protocol + '//' + window.location.host;
}

const API_BASE_URL = getAuthApiBaseUrl();

// Создаем экземпляр axios с базовым URL
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Интерцептор для добавления токена к запросам
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    console.log('🔍 [INTERCEPTOR] Request URL:', config.url);
    console.log('🔍 [INTERCEPTOR] Request method:', config.method);
    console.log('🔍 [INTERCEPTOR] Token from localStorage:', token);
    console.log('🔍 [INTERCEPTOR] Current headers:', config.headers);
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('✅ [INTERCEPTOR] Token added to headers:', config.headers.Authorization);
    } else {
      console.log('❌ [INTERCEPTOR] No token found in localStorage');
    }
    
    console.log('🔍 [INTERCEPTOR] Final headers:', config.headers);
    return config;
  },
  (error) => {
    console.log('❌ [INTERCEPTOR] Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок аутентификации
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/users/auth/token/refresh/`, {
            refresh: refreshToken,
          });
          
          const { access } = response.data;
          localStorage.setItem('access_token', access);
          
          originalRequest.headers.Authorization = `Bearer ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          // Если refresh token недействителен, выходим из системы
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    }
    
    return Promise.reject(error);
  }
);

// Функции аутентификации
export const authAPI = {
  // Регистрация
  register: async (userData) => {
    const response = await api.post('/api/users/auth/registration/', userData);
    return response.data;
  },

  // Вход
  login: async (credentials) => {
    const response = await api.post('/api/users/auth/login/', credentials);
    return response.data;
  },

  // Выход
  logout: async () => {
    const response = await api.post('/api/users/auth/logout/');
    return response.data;
  },

  // Обновление токена
  refreshToken: async (refreshToken) => {
    const response = await api.post('/api/users/auth/token/refresh/', {
      refresh: refreshToken,
    });
    return response.data;
  },

  // Получение данных пользователя
  getUser: async () => {
    const response = await api.get('/api/users/auth/user/');
    return response.data;
  },

  // Обновление профиля
  updateProfile: async (profileData) => {
    console.log('🚀 [UPDATE_PROFILE] Starting profile update with data:', profileData);
    console.log('🚀 [UPDATE_PROFILE] Current localStorage access_token:', localStorage.getItem('access_token'));
    console.log('🚀 [UPDATE_PROFILE] Current localStorage refresh_token:', localStorage.getItem('refresh_token'));
    
    const response = await api.patch('/api/users/profile/', profileData);
    console.log('✅ [UPDATE_PROFILE] Profile update successful:', response.data);
    return response.data;
  },

  // Получение дашборда
  getDashboard: async () => {
    const response = await api.get('/api/users/dashboard/');
    return response.data;
  },

  // Получение активности пользователя
  getUserActivities: async () => {
    const response = await api.get('/api/users/activities/');
    return response.data;
  },

  // Связывание социального аккаунта
  linkSocialAccount: async (socialData) => {
    const response = await api.post('/api/users/social/link/', socialData);
    return response.data;
  },

  // Отвязывание социального аккаунта
  unlinkSocialAccount: async (provider) => {
    const response = await api.delete(`/api/users/social/unlink/${provider}/`);
    return response.data;
  },

  // Социальная аутентификация
  socialLogin: async (provider, accessToken) => {
    const endpointMap = {
      google: '/api/users/auth/google/',
      facebook: '/api/users/auth/facebook/',
      apple: '/api/users/auth/apple/',
    };
    
    const endpoint = endpointMap[provider.toLowerCase()];
    if (!endpoint) {
      throw new Error(`Unsupported provider: ${provider}`);
    }
    
    const response = await api.post(endpoint, {
      access_token: accessToken,
    });
    return response.data;
  },

  googleLogin: async (accessToken) => {
    // Отправляем access_token на backend (правильный путь с /api)
    const response = await api.post('/api/users/auth/google/', {
      access_token: accessToken,
    });
    return response.data;
  },

  appleWebLogin: async ({ code, idToken }) => {
    const payload = {};
    if (code) {
      payload.code = code;
    }
    if (idToken) {
      payload.id_token = idToken;
    }

    const response = await api.post('/api/users/auth/apple/', payload);
    return response.data;
  },

  facebookLogin: async (accessToken) => {
    const response = await api.post('/api/users/auth/facebook/', {
      access_token: accessToken,
    });
    return response.data;
  },

  appleLogin: async (accessToken) => {
    const response = await api.post('/api/users/auth/apple/', {
      access_token: accessToken,
    });
    return response.data;
  },


  // Админ: пользователи
  getAdminUsers: async (params = {}) => {
    const query = new URLSearchParams(params).toString();
    const url = query ? `/api/users/admin/users/?${query}` : '/api/users/admin/users/';
    const response = await api.get(url);
    return response.data;
  },

  updateAdminUser: async (userId, payload) => {
    const response = await api.patch(`/api/users/admin/users/${userId}/`, payload);
    return response.data;
  },

  getAdminUserDetail: async (userId) => {
    const response = await api.get(`/api/users/admin/users/${userId}/`);
    return response.data;
  },

  deleteAdminUser: async (userId, payload) => {
    const response = await api.delete(`/api/users/admin/users/${userId}/`, { data: payload });
    return response.data;
  },

  // Админ: активности/логи
  getAdminActivities: async (limit = 100) => {
    const response = await api.get(`/api/users/admin/activities/?limit=${limit}`);
    return response.data;
  },
  // Форма о здоровье
  getHealthForm: async () => {
    const response = await api.get('/api/users/health-form/');
    return response.data;
  },

  updateHealthForm: async (formData) => {
    const response = await api.patch('/api/users/health-form/', formData);
    return response.data;
  },

  // Документы
  getDocuments: async () => {
    const response = await api.get('/api/users/documents/');
    return response.data;
  },

  uploadDocument: async (formData) => {
    const response = await api.post('/api/users/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  // Загрузка фото профиля
  uploadProfilePhoto: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', 'profile_photo');
    const response = await api.post('/api/users/documents/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteMyAccount: async (payload) => {
    const response = await api.post('/api/users/account/delete/', payload);
    return response.data;
  },
};

// Утилиты для работы с токенами
export const tokenUtils = {
  setTokens: (accessToken, refreshToken) => {
    localStorage.setItem('access_token', accessToken);
    localStorage.setItem('refresh_token', refreshToken);
  },

  getAccessToken: () => {
    return localStorage.getItem('access_token');
  },

  getRefreshToken: () => {
    return localStorage.getItem('refresh_token');
  },

  removeTokens: () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },

  isAuthenticated: () => {
    return !!localStorage.getItem('access_token');
  },
};

// Утилиты для работы с пользователем
export const userUtils = {
  setUser: (user) => {
    localStorage.setItem('user', JSON.stringify(user));
  },

  getUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  removeUser: () => {
    localStorage.removeItem('user');
  },
};

export default api; 
